import { useEffect } from "react";
import { BarChart3, Camera, FileText, FolderOpen, ListChecks, Lock, Target } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, LEVEL_LABEL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLANS = {
  base: {
    badge: "Plan Base · $500 MXN/mes",
    title: "Desbloquea tu Auditoría Completa",
    text: "Esta auditoría base está disponible en el Plan Base. Incluye subida de evidencias fotográficas, reportes PDF y Plan de Acción automatizado por solo $500 MXN/mes. Contáctanos para activarlo.",
    button: "Solicitar Plan Base en WhatsApp",
    wa: (m) => `Hola, me interesa activar el Plan Base ($500 MXN/mes) de Vigía para la auditoría ${m?.name}.`,
    includes: [
      [Camera, "Subida de evidencias fotográficas en cada hallazgo físico."],
      [FileText, "Reportes PDF con tu logotipo y bloque de firmas."],
      [ListChecks, "Plan de Acción automatizado y Fix Tracking centralizado."],
    ],
  },
  pro: {
    badge: "Plan Pro · $800 MXN/mes",
    title: "Accede a la Inspección Profunda",
    text: "Los módulos de Inspección Exhaustiva detallada están disponibles en el Plan Pro. Diseñado para plantas industriales y certificaciones, por $800 MXN/mes. Contáctanos para subir de nivel.",
    button: "Solicitar Plan Pro en WhatsApp",
    wa: () => "Hola, me interesa conocer mas sobre los módulos de Inspección Exhaustiva y/o consultoría en el tema. Quiero activar el Plan Pro ($800 MXN/mes).",
    includes: [
      [BarChart3, "Evaluación capítulo por capítulo y punto por punto."],
      [FolderOpen, "Soporte para evidencias fotográficas y archivos PDF."],
      [FileText, "Reporte ejecutivo A4 con validez de pre-auditoría oficial y bloque de firmas."],
      [Target, "Integración automática al Plan de Acción y Fix Tracking centralizado."],
    ],
  },
};

export const PremiumDialog = ({ module, onOpenChange }) => {
  const { user } = useAuth();
  const plan = PLANS[module?.level] ?? PLANS.pro;
  const href = `https://wa.me/${process.env.REACT_APP_WHATSAPP_NUMBER}?text=${encodeURIComponent(plan.wa(module))}`;
  useEffect(() => {
    if (module?.id) api.post("/premium-interest", { module_id: module.id }).catch(() => {});
  }, [module?.id]);
  return (
    <Dialog open={!!module} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111722] border-white/10 rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-lenis-prevent data-testid="premium-dialog">
        <DialogHeader className="text-left">
          <span className="inline-flex w-fit items-center gap-1.5 px-3 py-1 bg-[#F59E0B] text-[#0B0F17] font-mono text-[11px] uppercase tracking-[0.14em] font-medium" data-testid="premium-badge">
            <Lock className="w-3.5 h-3.5" /> {plan.badge}
          </span>
          <DialogTitle className="font-display text-2xl sm:text-3xl font-extrabold leading-tight pt-3" data-testid="premium-dialog-title">
            {plan.title}
          </DialogTitle>
          <DialogDescription className="text-[#F5F1E8]/75 text-base leading-relaxed" data-testid="premium-dialog-text">
            {plan.text}
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#F5F1E8]/60">{module?.name} · {module?.total} reactivos · incluye</div>
          <ul className="mt-3 space-y-3" data-testid="premium-includes">
            {plan.includes.map(([Icon, t]) => (
              <li key={t} className="flex items-start gap-3 text-sm"><Icon className="w-5 h-5 text-[#F59E0B] shrink-0" strokeWidth={1.6} /> {t}</li>
            ))}
          </ul>
        </div>
        <div className="mt-2 border border-[#F59E0B]/40 bg-[#F59E0B]/[0.07] p-4 flex items-start gap-3 text-sm" data-testid="premium-access-status">
          <Lock className="w-4 h-4 text-[#F59E0B] shrink-0 mt-0.5" />
          <span>Tu plan actual: <strong>{LEVEL_LABEL[user?.subscription_level ?? "free"]}</strong>. Este módulo requiere el {LEVEL_LABEL[module?.level]}.</span>
        </div>
        <div className="mt-2 flex flex-col sm:flex-row gap-3">
          <a href={href} target="_blank" rel="noreferrer" data-testid="premium-whatsapp-button" className="flex-1 inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#25D366] text-[#0B0F17] font-semibold text-sm hover:bg-[#F5F1E8] transition-colors">
            {plan.button}
          </a>
          <button type="button" onClick={() => onOpenChange(false)} data-testid="premium-back-button" className="px-5 py-3 rounded-full border border-white/20 text-sm font-semibold hover:border-white/50 transition-colors">
            Volver al catálogo
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
